import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  LineChart, HomeIcon, Banknote, Users2, PhoneCall, Mail as MailIcon,
  Building2, Rocket, GraduationCap, MapPin, ArrowUpRight
} from "lucide-react";

const CLUSTERS = [
  {
    key: "stock",
    n: "01",
    icon: LineChart,
    title: "Stock Market & Trader Data",
    subs: [
      "Stock market traders database — F&O, intraday, positional, HNI and retail investor lists",
      "Share market data provider and stock market calling data provider — dialler-ready CSVs with SEBI-sensitive segmentation",
      "Data provider for stock market broking, PMS and advisory teams — including forex data provider services and US stock market data on request",
      "For quick evaluation we share sample stock market calling data PDFs and can quote a stock market data provider number for your exact geography",
    ],
    tags: ["stock market data provider", "share market data provider", "demat account data provider", "traders data provider", "forex data provider", "best stock data provider", "data provider for stock market", "stock market calling data provider", "stock market calling data pdf"],
  },
  {
    key: "realestate",
    n: "02",
    icon: HomeIcon,
    title: "Real Estate Data & Calling Lists",
    subs: [
      "Real estate data provider for developers, brokers and channel partners — RERA-verified projects and city-wise buyer intent",
      "Real estate customer database and real estate leads database — with data for real estate calling in Mumbai, Delhi NCR, Bangalore, Pune and Jaipur",
      "Real estate data vendors in Mumbai know locality-level buyer profiles — we go one better with pincode-wise segmentation and calling data for real estate India-wide",
    ],
    tags: ["real estate data provider", "real estate customer database", "real estate leads database", "real estate calling data", "calling data for real estate mumbai", "calling data for real estate india", "data for real estate calling", "data vendors for real estate"],
  },
  {
    key: "loan",
    n: "03",
    icon: Banknote,
    title: "Loan & Credit Data",
    subs: [
      "Personal loan data provider, home loan data provider and car loan data provider — segmented by ticket size, city and income band",
      "Loan data provider in Delhi, Mumbai and Bangalore for NBFCs, DSAs and fintech lenders",
      "Consent-based borrower lists that respect DPDP norms",
    ],
    tags: ["personal loan data provider", "home loan data provider", "car loan data provider", "loan data provider", "loan data provider in delhi"],
  },
  {
    key: "b2b",
    n: "04",
    icon: Users2,
    title: "B2B, B2C & Business Data",
    subs: [
      "B2B data providers in India offering firmographics, decision-maker contacts and industry segmentation",
      "B2B database providers for SaaS outbound, B2B list providers for account-based marketing, and B2B contact database provider services for enterprise lead-gen",
      "Companies database India covering newly registered businesses, newly incorporated companies and existing MSME/mid-market accounts",
    ],
    tags: ["b2b data providers", "b2b database providers", "b2b list providers", "b2b data solutions", "b2b database companies", "b2b contact database provider", "b2b data providers india", "b2b database providers in india", "business data provider", "company data provider", "companies database india", "newly registered companies", "newly registered businesses", "newly incorporated companies"],
  },
  {
    key: "telecalling",
    n: "05",
    icon: PhoneCall,
    title: "Telecalling & Mobile Number Databases",
    subs: [
      "Mobile number database, mobile phone number list and pincode-wise mobile number database for outbound calling teams",
      "Data vendor for call center operations — telecalling database for calling with clean, dialler-ready mobile number data",
      "If you need data for calling — real estate, loans, BFSI or ed-tech — we scope the data purchase for calling in one conversation and quote a transparent price",
      "You can also buy phone number database splits by city, category and interest — including customer mobile number data with clear opt-out handling",
    ],
    tags: ["mobile number database", "mobile number database provider", "mobile number data provider", "mobile phone number list", "pincode wise mobile number database", "customer mobile number data", "mobile number data", "mobile number data for calling", "mobile number data list", "mobile no database", "database mobile number", "database phone number", "phone number database", "phone number database india", "telecalling database", "telecalling data", "calling database", "database for calling", "data for calling", "data for telecalling", "calling data number", "data calling number", "online data for calling", "data purchase for calling", "need data for calling", "buy phone number database", "data vendor for call center", "calling data provider"],
  },
  {
    key: "email",
    n: "06",
    icon: MailIcon,
    title: "Email Databases",
    subs: [
      "Email database providers in India for cold outbound, newsletters and lifecycle marketing",
      "Email data provider services with verified corporate inboxes and role-based segmentation",
      "Bulk database provider deliveries with SPF-friendly formatting and de-duplication reports",
    ],
    tags: ["email database providers", "email database providers in india", "email data provider", "b2b data providers", "bulk database provider", "bulk database", "bulk data provider"],
  },
  {
    key: "gst",
    n: "07",
    icon: Building2,
    title: "GST & Trader Data",
    subs: [
      "GST data provider — GSTIN-linked business intelligence, filed-returns geography and turnover bands",
      "Trader data provider services covering wholesalers, distributors and export-oriented MSMEs",
    ],
    tags: ["gst data provider", "traders data provider"],
  },
  {
    key: "newco",
    n: "08",
    icon: Rocket,
    title: "Newly Registered Company Data",
    subs: [
      "Fresh MCA incorporations, directors, authorized capital, industry code and registered city — updated weekly",
      "Ideal for CA firms, banking RMs, insurance and SaaS teams targeting newly incorporated companies",
    ],
    tags: ["newly registered companies", "newly registered businesses", "newly incorporated companies"],
  },
  {
    key: "student",
    n: "09",
    icon: GraduationCap,
    title: "Student Data",
    subs: [
      "Student data provider services for ed-tech, test-prep and study-abroad counsellors",
      "Students data provider records by exam, class, board and city — used with consent frameworks appropriate for minors",
    ],
    tags: ["student data provider", "students data provider"],
  },
];

const LOCATIONS = [
  { city: "Mumbai", note: "Database provider in Mumbai — real estate data vendors, BFSI leads and BKC-based operations" },
  { city: "Delhi NCR", note: "Data provider in Delhi — Delhi NCR database and Delhi NCR companies database" },
  { city: "Bangalore", note: "Database providers in Bangalore — SaaS, IT services and startup founder data" },
  { city: "Chennai", note: "Database providers in Chennai and telecalling database Chennai — BFSI, manufacturing and auto ancillaries" },
  { city: "Indore", note: "Stock market data provider in Indore and MP-wide trader lists" },
  { city: "Jaipur", note: "Calling data Jaipur — real estate, retail and consumer segments" },
  { city: "Pan India", note: "Pan India database and pan India data — every state, 700+ districts, 19,000+ pincodes" },
];

export default function KeywordSections() {
  return (
    <section
      data-testid="keyword-sections"
      aria-label="Data provider services by category"
      className="relative py-24 md:py-32 border-t border-slate-100 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-16 md:mb-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Coverage</div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl font-light tracking-tight text-slate-900 leading-[1.02]">
              A <span className="font-serif-editorial italic">data provider company</span>,<br /> built for every category.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 text-base md:text-lg text-slate-600 leading-relaxed">
            Datorra is a genuine database provider and database seller trusted by growth teams across
            BFSI, real estate, stock market, telecalling, e-commerce and enterprise B2B. As one of the
            more established data vendors in India and data providers in India, every list is scoped,
            priced transparently and delivered in Excel or CSV.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CLUSTERS.map((c) => {
            const Icon = c.icon;
            return (
              <motion.article
                key={c.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6 }}
                data-testid={`kw-cluster-${c.key}`}
                className="flex flex-col bg-[#fafafa] border border-slate-100 rounded-3xl p-7 md:p-8 hover:bg-white hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-white border border-slate-100 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-slate-900" strokeWidth={1.5} />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400 font-medium">/{c.n}</div>
                </div>
                <h3 className="mt-8 font-display text-xl md:text-2xl font-medium text-slate-900 tracking-tight">
                  {c.title}
                </h3>
                <ul className="mt-4 space-y-2.5 text-sm text-slate-600 leading-relaxed list-disc pl-4 marker:text-blue-500">
                  {c.subs.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
                <Link
                  to="/contact"
                  className="mt-6 self-start inline-flex items-center gap-1.5 text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors"
                >
                  Enquire about {c.title.toLowerCase()}
                  <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} />
                </Link>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-24 md:mt-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Locations</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
                Data providers near you, <span className="font-serif-editorial italic">across India</span>.
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9 text-base text-slate-600 leading-relaxed">
              Datorra delivers Pan India — with focused city expertise where growth teams need it most.
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {LOCATIONS.map((l, i) => (
              <motion.div
                key={l.city}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
                className="flex items-start gap-4 bg-white border border-slate-100 rounded-2xl p-5 hover:border-blue-100 transition-colors"
              >
                <div className="mt-0.5 w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-blue-600" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="font-display text-lg font-medium text-slate-900">{l.city}</div>
                  <div className="mt-1 text-sm text-slate-500 leading-relaxed">{l.note}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
